import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { OrganizationConfiguration } from "../../models/OrganizationConfiguration/OrganizationConfiguration";
import OrganizationsRepository from "../../repositories/OrganizationsRepository";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Trash2 } from "lucide-react";
import ConfirmationModal from "../common/ConfirmationModal";
import { toast } from "sonner";

interface DropboxApiAuthConfigurationSectionProps {
  disabled?: boolean;
}

const DropboxApiAuthConfigurationSection: React.FC<DropboxApiAuthConfigurationSectionProps> = ({
  disabled = false,
}) => {
  const form = useFormContext<OrganizationConfiguration>();
  const organizationsRepository = new OrganizationsRepository();
  const [deleteRefreshTokenId, setDeleteRefreshTokenId] = useState<string | null>(null);
  const [deleteRefreshTokenName, setDeleteRefreshTokenName] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const dropboxRefreshTokens = form.watch("dropboxRefreshTokens");

  const handleDeleteDropboxRefreshToken = (id: string, name: string) => {
    setDeleteRefreshTokenId(id);
    setDeleteRefreshTokenName(name);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteRefreshTokenId) {
      try {
        await organizationsRepository.deleteDropboxRefreshToken(deleteRefreshTokenId);
        form.setValue("dropboxRefreshTokens",
          dropboxRefreshTokens?.filter(
            (token) => token.id !== deleteRefreshTokenId
          ) ?? null
        );
      } catch {
        toast.error("Failed to delete the Dropbox refresh token");
      }
    }
    handleCloseDeleteModal();
  };

  const handleCloseDeleteModal = () => {
    setDeleteRefreshTokenId(null);
    setDeleteRefreshTokenName(null);
    setIsDeleteModalOpen(false);
  };

  return (
    <>
      <Card className="p-0 gap-0 flex flex-col flex-1 min-h-0">
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[10px] px-1.5 py-3"></TableHead>
              <TableHead className="px-1.5 py-3">Name</TableHead>
              <TableHead className="w-[60px] px-1.5 py-3"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dropboxRefreshTokens != null &&
              dropboxRefreshTokens.map((dropboxRefreshToken) => (
                <TableRow key={dropboxRefreshToken.name}>
                  <TableCell />
                  <TableCell>
                    <span className="text-sm">
                      {dropboxRefreshToken.name}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      type="button"
                      disabled={disabled}
                      aria-label="Delete"
                      variant="ghost"
                      size="icon-sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() =>
                        handleDeleteDropboxRefreshToken(dropboxRefreshToken.id, dropboxRefreshToken.name)
                      }
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this refresh token?"
        statusColor="danger"
        buttonText="Delete"
      >
        <div className="flex flex-wrap">
          <Badge variant="outline-primary">{deleteRefreshTokenName || ""}</Badge>
        </div>
      </ConfirmationModal>
    </>
  );
};

export default DropboxApiAuthConfigurationSection;
